import React, { useEffect, useState } from 'react';
import { getSnippets, addSnippet, updateSnippet, api } from '../utils/api';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import { motion } from 'framer-motion';

// Add this helper (or in api.js)
export async function deleteSnippet(keyword) {
  const res = await api.delete("/admin/delete_snippet", { data: { keyword } });
  return res.data;
}

export default function Admin() {
  const [snippets, setSnippets] = useState([]);
  const [selected, setSelected] = useState(null);
  const [editContent, setEditContent] = useState('');
  const [editKeyword, setEditKeyword] = useState('');
  const [message, setMessage] = useState('');
  const [addMode, setAddMode] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    fetchSnippets();
  }, []);

  async function fetchSnippets() {
    const data = await getSnippets();
    setSnippets(data);
    // Auto-select first snippet if nothing selected
    if (data.length && !selected && !addMode) setSelected(data[0]);
  }

  useEffect(() => {
    if (selected) {
      setEditKeyword(selected.keyword);
      setEditContent(selected.content);
    }
  }, [selected]);

  const handleSelectSnippet = (snippet) => {
    setSelected(snippet);
    setAddMode(false);
    setMessage('');
    setConfirmDelete(false);
  };

  const handleUpdate = async () => {
    if (!editKeyword.trim() || !editContent.trim()) {
      setMessage('Content/Keyword cannot be empty!');
      return;
    }
    try {
      await updateSnippet(editKeyword, editContent);
      setMessage('Snippet updated!');
      await fetchSnippets();
    } catch {
      setMessage('Update failed.');
    }
  };

  const handleAdd = async () => {
    if (!editKeyword.trim() || !editContent.trim()) {
      setMessage('Keyword and content required!');
      return;
    }
    try {
      await addSnippet(editKeyword, editContent);
      setMessage('Snippet added!');
      setEditKeyword('');
      setEditContent('');
      setAddMode(false);
      await fetchSnippets();
    } catch {
      setMessage('Failed to add snippet. Duplicate keyword?');
    }
  };

  const handleDelete = async () => {
    if (!selected) return;
    try {
      await deleteSnippet(selected.keyword);
      setMessage('Snippet deleted!');
      setSelected(null);
      setConfirmDelete(false);
      await fetchSnippets();
    } catch {
      setMessage('Failed to delete snippet.');
    }
  };

  // Fullscreen two-panel layout, responsive
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      style={{
        display: 'flex',
        flexDirection: 'row',
        minHeight: '85vh',
        width: '100vw',
        maxWidth: '100%',
      }}
    >
      {/* LEFT SIDEBAR  */}
      <Paper elevation={8}
        sx={{
          p: 2,
          borderRadius: 0,
          borderTopLeftRadius: 12,
          borderBottomLeftRadius: 12,
          minWidth: 290,
          width: 310,
          maxWidth: 350,
          overflowY: 'auto',
          height: '100%',
          bgcolor: '#2b2f45'
        }}>
        <Typography variant="h6" sx={{ mb: 2, mt: 1 }}>Snippets</Typography>
        <List>
          {snippets.map((s) => (
            <ListItem key={s.keyword} disablePadding>
              <ListItemButton
                selected={selected && selected.keyword === s.keyword}
                onClick={() => handleSelectSnippet(s)}
              >
                <ListItemText primary={s.keyword} />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
        <Button sx={{ mt: 3, mb: 2 }} fullWidth color="success" variant="contained" onClick={() => {
          setSelected(null);
          setAddMode(true);
          setEditKeyword('');
          setEditContent('');
          setMessage('');
        }}>
          + Add New
        </Button>
      </Paper>

      {/* RIGHT PANEL */}
      <Box sx={{
        flex: 1,
        p: { xs: 1, sm: 5 },
        minHeight: '85vh',
        background: "linear-gradient(130deg, #181a23 60%, #212842 100%)"
      }}>
        <Paper elevation={8} sx={{
          p: { xs: 2, sm: 4 },
          minHeight: 500,
          borderRadius: 5,
          maxWidth: 720,
          margin: '0 auto'
        }}>
          <Typography variant="h6" sx={{ mb: 3 }}>
            {addMode
              ? "Add New Snippet"
              : selected
                ? `Edit: ${selected.keyword}`
                : "Select a snippet"}
          </Typography>

          {(addMode || selected) && (
            <Box component="form" autoComplete="off" sx={{ display: 'flex', flexDirection: 'column', gap: 3, mb: 2 }}>
              <TextField
                label="Keyword"
                value={editKeyword}
                onChange={e => setEditKeyword(e.target.value)}
                disabled={!addMode}
                required
                sx={{ maxWidth: 400 }}
              />
              <TextField
                label="Content"
                value={editContent}
                onChange={e => setEditContent(e.target.value)}
                multiline
                minRows={6}
                fullWidth
              />
              <Box sx={{ display: 'flex', gap: 2 }}>
                {addMode
                  ? <Button variant="contained" color="success" onClick={handleAdd}>Add Snippet</Button>
                  : <>
                      <Button variant="contained" color="primary" onClick={handleUpdate}>Update</Button>
                      <Button variant="outlined" color="error" onClick={() => setConfirmDelete(true)}>
                        Delete
                      </Button>
                    </>
                }
              </Box>
              {message && <Typography sx={{ color: message.includes('fail') ? 'red' : '#39e178' }}>{message}</Typography>}
            </Box>
          )}

          {confirmDelete && (
            <Paper sx={{ p:2, background: "#261314", border: '2px solid #f44', mt: 2 }}>
              <Typography color="error" sx={{mb:2}}>Are you sure you want to delete "<b>{selected?.keyword}</b>" forever?</Typography>
              <Button onClick={handleDelete} color="error" variant="contained" sx={{mr:1}}>Yes, Delete</Button>
              <Button onClick={()=>setConfirmDelete(false)} color="secondary" variant="outlined">Cancel</Button>
            </Paper>
          )}

          {!addMode && !selected && (
            <Typography color="text.secondary">Select a snippet to view/edit, or click "Add New." </Typography>
          )}
        </Paper>
      </Box>
    </motion.div>
  );
}
